import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { BookOpen, ArrowLeft, Search, Loader2 } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

function KBHeader() {
  return (
    <header className="bg-white border-b border-[#E2E8F0] py-4">
      <div className="container-custom flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3"><img src={LOGO_URL} alt="DCA" className="h-10 w-auto" /></Link>
        <Link to="/" className="text-sm text-[#475569] hover:text-[#1D4ED8] flex items-center gap-2"><ArrowLeft size={16} /> Back to Website</Link>
      </div>
    </header>
  );
}

export function KnowledgeBasePage() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [loading, setLoading] = useState(true);

  const load = async (search = '', category = '') => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/knowledge-base`, {
        params: { ...(search && { q: search }), ...(category && { category }) },
      });
      setItems(res.data.items || []);
      setCategories(res.data.categories || []);
    } catch (_) {} // eslint-disable-line no-empty
    setLoading(false);
  };

  useEffect(() => { load(q, activeCategory); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="kb-public-page">
      <KBHeader />
      <main className="container-custom py-12 max-w-4xl">
        <h1 className="text-3xl sm:text-4xl font-bold text-[#0A0F1C] mb-2 flex items-center gap-2"><BookOpen size={28} /> Knowledge Base</h1>
        <p className="text-[#475569] mb-8">Guides, frameworks, and answers to common questions.</p>

        <form
          onSubmit={(e) => { e.preventDefault(); setParams(q ? { q } : {}); load(q, activeCategory); }}
          className="mb-6"
        >
          <div className="relative max-w-xl">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search articles…"
              className="w-full border border-[#E2E8F0] rounded-md px-10 py-2.5 text-sm focus:border-[#1D4ED8] outline-none"
              data-testid="kb-public-search"
            />
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          </div>
        </form>

        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="kb-public-categories">
            <button
              onClick={() => { setActiveCategory(''); load(q, ''); }}
              className={`px-3 py-1.5 text-xs font-bold rounded-full border ${!activeCategory ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]' : 'bg-white text-[#475569] border-[#E2E8F0] hover:border-[#1D4ED8]'}`}
            >All</button>
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => { setActiveCategory(c); load(q, c); }}
                className={`px-3 py-1.5 text-xs font-bold rounded-full border ${activeCategory === c ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]' : 'bg-white text-[#475569] border-[#E2E8F0] hover:border-[#1D4ED8]'}`}
              >{c}</button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-[#94A3B8]"><Loader2 size={20} className="animate-spin inline mr-2" /> Loading…</div>
        ) : items.length === 0 ? (
          <p className="text-center text-[#94A3B8] py-12">No articles match your search.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="kb-public-list">
            {items.map((a) => (
              <Link
                key={a.article_id}
                to={`/knowledge-base/${a.slug}`}
                className="bg-white border border-[#E2E8F0] rounded-xl p-5 hover:shadow-md transition"
                data-testid={`kb-public-card-${a.article_id}`}
              >
                <div className="text-[10px] font-bold text-[#1D4ED8] uppercase tracking-wider mb-1.5">{a.category}</div>
                <h3 className="font-bold text-[#0A0F1C] mb-1">{a.title}</h3>
                <p className="text-sm text-[#475569] line-clamp-3">{a.summary}</p>
                <div className="mt-3 text-xs text-[#94A3B8]">{(a.tags || []).slice(0, 3).join(' · ')}</div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export function KnowledgeBaseArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/knowledge-base/${slug}`)
      .then((res) => setArticle(res.data))
      .catch((err) => setError(err.response?.status === 404 ? 'Article not found.' : 'Could not load article.'))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="kb-article-page">
      <KBHeader />
      <main className="container-custom py-12 max-w-3xl">
        <Link to="/knowledge-base" className="text-sm text-[#1D4ED8] hover:underline inline-flex items-center gap-1 mb-6">
          <ArrowLeft size={14} /> All articles
        </Link>
        {loading && <div className="text-center py-12 text-[#94A3B8]"><Loader2 size={20} className="animate-spin inline mr-2" /> Loading…</div>}
        {error && <p className="text-center text-red-600 py-12">{error}</p>}
        {article && (
          <article className="bg-white border border-[#E2E8F0] rounded-2xl p-8" data-testid="kb-article-content">
            <div className="text-[10px] font-bold text-[#1D4ED8] uppercase tracking-wider mb-2">{article.category}</div>
            <h1 className="text-3xl font-bold text-[#0A0F1C] mb-3" data-testid="kb-article-title">{article.title}</h1>
            {article.summary && <p className="text-[#475569] mb-6">{article.summary}</p>}
            <div className="prose prose-slate max-w-none text-[#1F2937] whitespace-pre-wrap leading-relaxed text-sm">
              {article.body}
            </div>
            {(article.tags || []).length > 0 && (
              <div className="mt-6 pt-4 border-t border-[#E2E8F0] text-xs text-[#94A3B8]">
                Tags: {(article.tags || []).join(' · ')}
              </div>
            )}
          </article>
        )}
      </main>
    </div>
  );
}

export default KnowledgeBasePage;
